"use client";

import CheckmarkIcon from "public/next-public/common/icons/toast-success-check-icon.svg";
import React from "react";
import styles from "./checkmark.module.scss";

export type CheckmarkSize = "small" | "medium" | "large" | "extraLarge";
export type CheckmarkTone = "inherit" | "success" | "successStrong";

interface CheckmarkProps {
  /**
   * Fixed size. Omit to let CSS drive the size: set `--checkmark-size` on the element
   * through `className`, which allows a single instance to change size responsively.
   */
  size?: CheckmarkSize;
  tone?: CheckmarkTone;
  className?: string;
  /**
   * When provided, the checkmark is exposed to assistive tech as role="img" with this label.
   * When omitted (default), it is decorative (aria-hidden) — use that when adjacent text already
   * conveys meaning, as in the review success message.
   */
  "aria-label"?: string;
}

const SIZE_PX: Record<CheckmarkSize, number> = {
  small: 16,
  medium: 20,
  large: 24,
  extraLarge: 32,
};

function Checkmark({ size, tone = "inherit", className, "aria-label": ariaLabel }: CheckmarkProps) {
  const classNames = [styles.Checkmark, styles[tone], className || ""].filter(Boolean).join(" ");
  const labelled = typeof ariaLabel === "string" && ariaLabel.length > 0;
  const sizeStyle = size ? ({ "--checkmark-size": `${SIZE_PX[size]}px` } as React.CSSProperties) : undefined;

  return (
    <span
      className={classNames}
      style={sizeStyle}
      role={labelled ? "img" : undefined}
      aria-label={labelled ? ariaLabel : undefined}
    >
      <CheckmarkIcon aria-hidden={labelled ? undefined : true} />
    </span>
  );
}

export default Checkmark;
